import React, { useEffect, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  StockHeroBand,
  StockHeroIntro,
  StockPageTitle,
  StockPrimaryButton,
  StockQuietButton,
} from 'src/components/stock/StockPageChrome'
import { useWastageTodayCost } from 'src/components/stock/WasteHomeCard'
import { WasteHistory } from 'src/components/stock/WasteHistory'
import { WasteRecordForm } from 'src/components/stock/WasteRecordForm'
import {
  WasteFilters,
  wastageProductOptions,
} from 'src/components/stock/WasteFilters'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import StockLimit from './StockLimit'
import {
  WASTAGE_COPY,
  WASTAGE_EMPTY,
  wastageLondonMonthRange,
  wastageLondonToday,
  wastageProductFromSearch,
  wastageRangeFromSearch,
  wastageBackPath,
  wastageListWithJustSaved,
  wastageTodayCostLabel,
  wastageUserOptions,
} from 'src/constants/wastage'
import {
  deleteWastageEntry,
  downloadWastageCsv,
  editWastageEntry,
  getWastageEntry,
  listWastageEntries,
  saveWastageCsvFile,
} from 'src/utils/wastageApi'
import {
  FREE_STOCK_DESKTOP_LEAD,
  FREE_STOCK_UPGRADE_CTA,
} from 'src/utils/growthEngineStockHonestFirstTry'
import { OLLIE_FAB_CONTENT_CLEARANCE } from 'src/components/ollie/ollieFabPlacement'
import styled from 'styled-components/macro'

const stockLimitCopy = {
  title: 'Stock',
  subheading: FREE_STOCK_DESKTOP_LEAD,
  cardTitle: 'Full reports live here. A simple count starts in the app.',
  benefits: [
    'Capture stock and wastage quickly on mobile with your team',
    'View full stock, wastage, and valuation reports on desktop',
    'Lock periods to keep reporting accurate and audit-ready',
    'Track trends over time to improve GP and reduce losses',
  ],
  previewOverlay: 'Desktop reporting preview',
  primaryCta: FREE_STOCK_UPGRADE_CTA,
}

/** True when this venue has no waste entries yet. Undefined while that is loading. */
export function useWastePageEmpty(
  venueId,
  { listEntries = listWastageEntries, reloadKey = 0 } = {},
) {
  const [empty, setEmpty] = useState(undefined)

  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    listEntries(venueId)
      .then(data => {
        if (!cancelled) {
          setEmpty(data?.count === 0 && !Number(data?.removedCount))
        }
        return null
      })
      .catch(() => {
        if (!cancelled) setEmpty(false)
        return null
      })
    return () => {
      cancelled = true
    }
  }, [venueId, listEntries, reloadKey])

  return empty
}

/**
 * Entries and totals for the selected dates.
 * A product or dish filter asks again so the total is for that one only.
 */
export function useWasteFilter(
  venueId,
  {
    from,
    to,
    productId,
    recipeId,
    reason = '',
    userId = '',
    includeRemoved = false,
    listEntries = listWastageEntries,
    reloadKey = 0,
  } = {},
) {
  const [catalog, setCatalog] = useState(undefined)
  const [filtered, setFiltered] = useState(undefined)

  useEffect(() => {
    if (!venueId || !from || !to || from > to) return undefined
    let cancelled = false
    const query = { from, to }
    if (includeRemoved) query.includeRemoved = true
    listEntries(venueId, query)
      .then(data => {
        if (!cancelled) {
          setCatalog({
            entries: Array.isArray(data?.entries) ? data.entries : [],
            totals: data?.totals ?? null,
          })
        }
        return null
      })
      .catch(() => {
        if (!cancelled) setCatalog({ entries: [], totals: null })
        return null
      })
    return () => {
      cancelled = true
    }
  }, [venueId, from, to, includeRemoved, listEntries, reloadKey])

  useEffect(() => {
    if (
      (!productId && !recipeId && !reason && !userId) ||
      !venueId ||
      !from ||
      !to ||
      from > to
    ) {
      setFiltered(undefined)
      return undefined
    }
    let cancelled = false
    const query = { from, to }
    if (recipeId) query.recipe = recipeId
    else if (productId) query.product = productId
    if (reason) query.reason = reason
    if (userId) query.user = userId
    if (includeRemoved) query.includeRemoved = true
    listEntries(venueId, query)
      .then(data => {
        if (!cancelled) {
          setFiltered({
            entries: Array.isArray(data?.entries) ? data.entries : [],
            totals: data?.totals ?? null,
          })
        }
        return null
      })
      .catch(() => {
        if (!cancelled) setFiltered({ entries: [], totals: null })
        return null
      })
    return () => {
      cancelled = true
    }
  }, [
    venueId,
    from,
    to,
    productId,
    recipeId,
    reason,
    userId,
    includeRemoved,
    listEntries,
    reloadKey,
  ])

  const source = productId || recipeId || reason || userId ? filtered : catalog
  return {
    entries: source?.entries,
    totals: source?.totals,
    options: wastageProductOptions(catalog?.entries),
    userOptions: wastageUserOptions(catalog?.entries),
  }
}

export const WastePage = ({
  venueId,
  listEntries = listWastageEntries,
  downloadCsv = downloadWastageCsv,
  saveCsv = saveWastageCsvFile,
  loadProducts,
  loadRecipes,
  createEntry,
  editEntry = editWastageEntry,
  removeEntry = deleteWastageEntry,
  loadEntry = getWastageEntry,
  today,
  from,
  to,
  recordProductId = '',
  backTo = '',
  onRecord,
}) => {
  const day = today || wastageLondonToday()
  const month = wastageLondonMonthRange(day)
  const [range, setRange] = useState(() => (from && to ? { from, to } : month))
  const [product, setProduct] = useState(null)
  const [reason, setReason] = useState('')
  const [userId, setUserId] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState('')
  const [formOpen, setFormOpen] = useState(Boolean(recordProductId))
  const [editing, setEditing] = useState(null)
  const [repeat, setRepeat] = useState(null)
  const [repeatToken, setRepeatToken] = useState(0)
  const [showRemoved, setShowRemoved] = useState(false)
  const [removeError, setRemoveError] = useState('')
  const [pendingRemove, setPendingRemove] = useState(null)
  const [revision, setRevision] = useState(0)
  const [justSaved, setJustSaved] = useState([])
  const pence = useWastageTodayCost(venueId, {
    listEntries,
    today: day,
    reloadKey: revision,
  })
  const isEmpty = useWastePageEmpty(venueId, {
    listEntries,
    reloadKey: revision,
  })
  const invalidRange = Boolean(range.from && range.to && range.from > range.to)
  const filter = useWasteFilter(venueId, {
    from: range.from,
    to: range.to,
    productId: product?.kind === 'recipe' ? '' : product?.id || '',
    recipeId: product?.kind === 'recipe' ? product.id : '',
    reason,
    userId,
    includeRemoved: showRemoved,
    listEntries,
    reloadKey: revision,
  })
  const costLabel = pence === undefined ? null : wastageTodayCostLabel(pence)
  const showHistory = isEmpty === false
  const historyEntries =
    showHistory && !invalidRange
      ? wastageListWithJustSaved(filter.entries, justSaved, {
          from: range.from,
          to: range.to,
          productId: product?.id || '',
        })
      : []

  const holdSaved = saved => {
    const id = saved?._id || saved?.id
    const day = saved?.businessDate
    if (!id || !day) return undefined
    const outside =
      range.from && range.to && (day < range.from || day > range.to)
    if (!outside) {
      setJustSaved(current =>
        current.filter(row => String(row._id || row.id) !== String(id)),
      )
      return undefined
    }
    setJustSaved(current => [
      ...current.filter(row => String(row._id || row.id) !== String(id)),
      saved,
    ])
    return loadEntry(venueId, id)
      .then(full => {
        if (full?.businessDate) {
          setJustSaved(current =>
            current.map(row =>
              String(row._id || row.id) === String(id) ? full : row,
            ),
          )
        }
        return null
      })
      .catch(() => null)
  }

  useEffect(() => {
    if (!pendingRemove) return undefined
    const onKey = event => {
      if (event.key === 'Escape') setPendingRemove(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pendingRemove])

  const onRemove = row => {
    const id = row?._id || row?.id
    if (!venueId || !id) return undefined
    setPendingRemove(row)
    return undefined
  }

  const confirmRemove = () => {
    const id = pendingRemove?._id || pendingRemove?.id
    if (!venueId || !id) return undefined
    setPendingRemove(null)
    setRemoveError('')
    return removeEntry(venueId, id)
      .then(() => {
        setJustSaved(current =>
          current.filter(row => String(row._id || row.id) !== String(id)),
        )
        if (String(editing?._id || editing?.id) === String(id)) {
          setEditing(null)
          setFormOpen(false)
        }
        setRevision(value => value + 1)
        return null
      })
      .catch(error => {
        setRemoveError(error?.message || 'Waste could not be removed')
        return null
      })
  }

  const onDownload = () => {
    if (!venueId || invalidRange || downloading) return undefined
    setDownloadError('')
    setDownloading(true)
    const filters = { from: range.from, to: range.to }
    if (product?.kind === 'recipe') filters.recipe = product.id
    else if (product?.id) filters.product = product.id
    if (reason) filters.reason = reason
    if (userId) filters.user = userId
    if (showRemoved) filters.includeRemoved = true
    return downloadCsv(venueId, filters)
      .then(file => {
        saveCsv(file)
        return null
      })
      .catch(error => {
        setDownloadError(error?.message || 'Waste could not be downloaded')
        return null
      })
      .finally(() => {
        setDownloading(false)
      })
  }

  return (
    <Page data-testid='waste-page'>
      <BackRow>
        <BackLink
          to={wastageBackPath(venueId, backTo)}
          data-testid='waste-back'
        >
          {WASTAGE_COPY.back}
        </BackLink>
      </BackRow>
      <StockHeroBand>
        <StockPageTitle>{WASTAGE_COPY.title}</StockPageTitle>
        <StockHeroIntro>{WASTAGE_EMPTY.body}</StockHeroIntro>
        {costLabel ? (
          <TodayCost data-testid='waste-today-cost'>{costLabel}</TodayCost>
        ) : null}
        {!formOpen && isEmpty !== undefined ? (
          <RecordRow>
            <StockPrimaryButton
              type='button'
              data-testid='waste-record'
              onClick={() => {
                setEditing(null)
                setRepeat(null)
                setFormOpen(true)
                if (onRecord) onRecord()
              }}
            >
              {WASTAGE_EMPTY.action}
            </StockPrimaryButton>
          </RecordRow>
        ) : null}
      </StockHeroBand>
      {formOpen ? (
        <WasteRecordForm
          venueId={venueId}
          today={day}
          listEntries={listEntries}
          loadProducts={loadProducts}
          loadRecipes={loadRecipes}
          createEntry={createEntry}
          updateEntry={editEntry}
          removeEntry={removeEntry}
          entry={editing}
          repeat={repeat}
          repeatToken={repeatToken}
          presetProductId={recordProductId}
          reloadKey={revision}
          onCancel={() => {
            setEditing(null)
            setRepeat(null)
            setFormOpen(false)
          }}
          onSaved={saved => {
            setEditing(null)
            setRepeat(null)
            holdSaved(saved)
            setRevision(value => value + 1)
          }}
          onUndone={id => {
            if (id) {
              setJustSaved(current =>
                current.filter(row => String(row._id || row.id) !== String(id)),
              )
            }
            setRevision(value => value + 1)
          }}
        />
      ) : null}
      {showHistory ? (
        <WasteFilters
          today={day}
          month={month}
          range={range}
          onRange={setRange}
          product={product}
          reason={reason}
          onReason={setReason}
          userId={userId}
          onUser={setUserId}
          userOptions={filter.userOptions || []}
          onProduct={id => {
            if (!id) {
              setProduct(null)
              return
            }
            const option = filter.options.find(
              item =>
                (item.kind === 'recipe' ? `recipe:${item.id}` : item.id) === id,
            )
            setProduct(
              option || {
                id,
                kind: 'product',
                title: 'Product',
                supplier: '',
              },
            )
          }}
          options={filter.options}
          totals={filter.totals}
          invalidRange={invalidRange}
          onDownload={onDownload}
          downloading={downloading}
          downloadError={downloadError}
          showRemoved={showRemoved}
          onShowRemoved={setShowRemoved}
        />
      ) : null}
      {removeError ? (
        <RemoveError data-testid='waste-remove-error'>
          {removeError}
        </RemoveError>
      ) : null}
      <WasteHistory
        entries={historyEntries}
        today={day}
        editingId={editing?._id || editing?.id || ''}
        onEdit={row => {
          setRepeat(null)
          setEditing(row)
          setFormOpen(true)
        }}
        onAgain={row => {
          setEditing(null)
          setRepeat(row)
          setRepeatToken(value => value + 1)
          setFormOpen(true)
        }}
        onRemove={onRemove}
      />
      {pendingRemove ? (
        <RemoveScrim
          data-testid='waste-remove-ask'
          onClick={() => setPendingRemove(null)}
        >
          <RemoveCard
            role='dialog'
            aria-modal='true'
            aria-labelledby='waste-remove-title'
            onClick={event => event.stopPropagation()}
          >
            <RemoveTitle id='waste-remove-title'>
              {WASTAGE_COPY.remove}
            </RemoveTitle>
            <RemoveQuestion>{WASTAGE_COPY.confirmRemove}</RemoveQuestion>
            <RemoveActions>
              <StockQuietButton
                type='button'
                data-testid='waste-remove-cancel'
                onClick={() => setPendingRemove(null)}
              >
                {WASTAGE_COPY.cancel}
              </StockQuietButton>
              <RemoveConfirm
                type='button'
                data-testid='waste-remove-confirm'
                onClick={confirmRemove}
              >
                {WASTAGE_COPY.remove}
              </RemoveConfirm>
            </RemoveActions>
          </RemoveCard>
        </RemoveScrim>
      ) : null}
    </Page>
  )
}

const WasteScreen = () => {
  const blocked = useIsBlocked('stock')
  const { venueId } = useParams()
  const location = useLocation()
  const { search } = location
  const period = wastageRangeFromSearch(search)
  const recordProductId = wastageProductFromSearch(search)
  const { currentPlan } =
    useSelector(state => state.venue?.venue?.usageLimits) ?? {}

  if (blocked) return <PermissionBlockScreen />

  if (
    currentPlan === 'Free' ||
    currentPlan === 'Starter' ||
    currentPlan === 'Growth'
  ) {
    return <StockLimit copy={stockLimitCopy} />
  }

  return (
    <WastePage
      venueId={venueId}
      from={period?.from}
      to={period?.to}
      recordProductId={recordProductId}
      backTo={location.state?.backTo}
    />
  )
}

const Page = styled.div`
  padding-bottom: ${OLLIE_FAB_CONTENT_CLEARANCE}px;
`

const BackRow = styled.div`
  margin-bottom: 0.85rem;
`

const BackLink = styled(StockQuietButton).attrs({
  as: Link,
  type: undefined,
})``

const TodayCost = styled.div`
  margin-top: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
`

const RecordRow = styled.div`
  margin-top: 1rem;
`

const RemoveError = styled.div`
  margin: 0 0 0.75rem;
  color: ${({ theme }) => theme.colours.chilliRed || '#b42318'};
  font-size: 0.9rem;
`

const RemoveScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(19, 45, 7, 0.35);
`

const RemoveCard = styled.div`
  width: min(100%, 26rem);
  padding: 1.25rem;
  border-radius: 12px;
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
`

const RemoveTitle = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  line-height: 1.3;
`

const RemoveQuestion = styled.p`
  margin: 0.6rem 0 0;
  font-size: 0.95rem;
  line-height: 1.45;
`

const RemoveActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.1rem;
`

const RemoveConfirm = styled(StockQuietButton)`
  border-color: ${({ theme }) => theme.colours.chilliRed || '#b42318'};
  color: ${({ theme }) => theme.colours.chilliRed || '#b42318'};
`

export default WasteScreen
